import { useState, type FormEvent } from 'react';
import { ArrowLeft, ArrowRight, Eye, EyeOff, ShieldCheck } from 'lucide-react';
import { Button, Input, Notice } from '../ui';
import { api, ApiError } from '../lib/api';
import { useResource } from '../lib/useResource';
import { useRevalidarSessao } from '../lib/session';
import { GoogleIcon, MicrosoftIcon } from './ProviderIcons';
import './login.css';

interface Provedores { google: boolean; microsoft: boolean }

/** Duas identidades, dois endpoints. Ver o comentário em `lib/session.tsx`. */
type Modo = 'workspace' | 'master';

/**
 * Entrada.
 *
 * É a única tela que alguém vê sem estar autenticado: errar aqui não degrada
 * uma função, impede o acesso. Por isso o cuidado com estados de erro é maior
 * do que no resto do produto, não menor.
 */
export function Login() {
  const revalidar = useRevalidarSessao();
  const { state } = useResource<Provedores>('/auth/providers', { vazio: () => false });
  const [modo, setModo] = useState<Modo>('workspace');
  const [erro, setErro] = useState<string | null>(null);
  const [entrando, setEntrando] = useState(false);
  const [verSenha, setVerSenha] = useState(false);

  const provedores = state.status === 'ready' ? state.data : { google: false, microsoft: false };
  const algumProvedor = modo === 'workspace' && (provedores.google || provedores.microsoft);

  function trocar(destino: Modo) {
    setModo(destino);
    setErro(null);
    setVerSenha(false);
  }

  async function entrar(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const dados = new FormData(event.currentTarget);
    setErro(null);
    setEntrando(true);
    try {
      await api(modo === 'master' ? '/auth/master/login' : '/auth/login', {
        method: 'POST',
        body: { email: String(dados.get('email') ?? ''), password: String(dados.get('password') ?? '') },
      });
      revalidar();
    } catch (falha) {
      /* Três mensagens diferentes para três causas diferentes. A versão
         anterior usava a mesma frase para senha errada e para bloqueio por
         tentativas — então quem já estava bloqueado continuava tentando, o que
         só estende o bloqueio. */
      if (falha instanceof ApiError && falha.status === 429) {
        setErro('Muitas tentativas seguidas. O acesso deste dispositivo fica bloqueado por 15 minutos.');
      } else if (falha instanceof ApiError && falha.status === 401 && modo === 'workspace') {
        /* O 401 de `/auth/login` cobre duas situações que exigem ações
           opostas: senha errada (tente de novo) e identidade sem vínculo com
           workspace nenhum (tentar de novo nunca vai funcionar). O servidor
           responde a mesma frase para as duas — de propósito, para não revelar
           quais e-mails existem. O que a tela pode fazer é apontar a saída. */
        setErro(`${falha.message} Se este e-mail é um acesso administrativo da Setter, use "Acesso administrativo Setter" abaixo — a conta de plataforma não entra por aqui.`);
      } else if (falha instanceof ApiError) {
        setErro(falha.message);
      } else {
        setErro('Sem conexão com o servidor. Verifique a rede e tente de novo.');
      }
      setEntrando(false);
    }
  }

  const master = modo === 'master';

  return (
    <main className="login" id="conteudo">
      <div className="login__cartao">
        <section className="login__painel">
          <header className="login__marca">
            <h1 className="login__titulo">{master ? 'Acesso administrativo Setter' : 'Entre na sua conta'}</h1>
            <p className="login__sub">
              {master
                ? 'Console da plataforma: criar empresas, convidar administradores e suspender contas. Não dá acesso a conversas.'
                : 'Use seu acesso corporativo ou as credenciais do workspace.'}
            </p>
          </header>

          {erro && <Notice tone="danger" title="Não foi possível entrar" live>{erro}</Notice>}

          {algumProvedor && (
            <div className="login__provedores">
              {/* Link e não botão: navegação para fora da aplicação. Um
                  <button> quebraria abrir em nova aba e o menu de contexto. */}
              {provedores.google && (
                <a className="login__provedor" href="/api/auth/google">
                  <GoogleIcon />
                  Entrar com sua conta Google
                </a>
              )}
              {provedores.microsoft ? (
                <a className="login__provedor" href="/api/auth/microsoft">
                  <MicrosoftIcon />
                  Entrar com sua conta Microsoft
                </a>
              ) : (
                <>
                  <button className="login__provedor" type="button" disabled aria-describedby="ms-nota">
                    <MicrosoftIcon />
                    Entrar com sua conta Microsoft
                  </button>
                  <p className="login__provedor-nota" id="ms-nota">
                    Microsoft ainda não está configurado neste workspace.
                  </p>
                </>
              )}
            </div>
          )}

          {algumProvedor && <p className="login__ou"><span>ou entre com</span></p>}

          {/* `key` força remontagem ao trocar de modo: sem isso o React reusa
              os mesmos <input> e a senha digitada em um formulário sobrevive
              no outro. */}
          <form onSubmit={entrar} className="login__form" key={modo}>
            <Input
              label="E-mail"
              name="email"
              type="email"
              required
              autoComplete="username"
              autoFocus
              placeholder={master ? 'voce@setter.global' : 'voce@empresa.com'}
            />
            <div className="senha">
              <Input
                label="Senha"
                name="password"
                type={verSenha ? 'text' : 'password'}
                required
                autoComplete="current-password"
              />
              {/* `aria-pressed` diz o ESTADO; o rótulo diz a AÇÃO. Um botão que
                  só troca de ícone deixa quem usa leitor de tela sem saber se a
                  senha está visível neste momento. */}
              <button
                type="button"
                className="senha__olho"
                aria-pressed={verSenha}
                aria-label={verSenha ? 'Ocultar senha' : 'Mostrar senha'}
                onClick={() => setVerSenha((v) => !v)}
              >
                {verSenha ? <EyeOff size={18} aria-hidden="true" /> : <Eye size={18} aria-hidden="true" />}
              </button>
            </div>
            <Button
              variant="primary"
              type="submit"
              loading={entrando}
              loadingLabel="Verificando suas credenciais"
              icon={master ? <ShieldCheck size={18} aria-hidden="true" /> : <ArrowRight size={18} aria-hidden="true" />}
            >
              {master ? 'Entrar no console' : 'Continuar'}
            </Button>
          </form>

          <div className="login__extras">
            {master ? (
              <button type="button" className="login__troca" onClick={() => trocar('workspace')}>
                <ArrowLeft size={16} aria-hidden="true" />
                Voltar para o acesso do workspace
              </button>
            ) : (
              <>
                <p className="login__ajuda">
                  Esqueceu a senha? Peça a um administrador do workspace para redefinir.
                </p>
                <button type="button" className="login__troca" onClick={() => trocar('master')}>
                  <ShieldCheck size={16} aria-hidden="true" />
                  Acesso administrativo Setter
                </button>
              </>
            )}
          </div>
        </section>

        <Hero />
      </div>
    </main>
  );
}

/** Painel de marca com o vídeo preservado da autenticação. */
function Hero() {
  const semMovimento =
    typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  return (
    <aside className="login__hero" aria-label="Apresentação da plataforma">
      <video
        className="login__video"
        src="/media/login-cover.mp4"
        autoPlay={!semMovimento}
        muted
        loop
        playsInline
        preload="metadata"
        aria-hidden="true"
        tabIndex={-1}
      />
      <div className="login__hero-texto">
        <span className="login__eyebrow">WhatsApp Business</span>
        <h2 className="login__hero-titulo">Atendimento que move negócios.</h2>
        <p className="login__hero-sub">
          Centralize conversas, números e automações em um workspace seguro.
        </p>
      </div>
    </aside>
  );
}
